import React, { useCallback, useEffect, useState } from 'react';
import {
  createValueLookup,
  deleteValueLookup,
  getValueLookup,
  listValueLookups,
  previewValueLookup,
  replaceValueLookupEntries,
  updateValueLookup,
} from '../../services/syncApi';

// Named value lookups translate Akeneo vocabulary (`finished_good`) into
// BigCommerce-friendly values (`product`). Each mapping with
// transform='value_lookup' points at one of these via value_lookup_id.
// Editing entries through this page bumps the lookup's version (DB
// trigger) which is the basis of optimistic locking on metadata edits.

const emptyEntry = () => ({ from_value: '', to_value: '' });

const formatErr = (e) => e?.response?.data?.detail || e?.message || 'request failed';
// FastAPI error bodies for DELETE-blocked-by-references use `detail: {message, mappings}`
// while validation errors are plain strings. Normalise to text for display.
const errToText = (raw) => {
  if (!raw) return '';
  if (typeof raw === 'string') return raw;
  if (raw.message) return raw.message;
  if (Array.isArray(raw)) return raw.map((r) => r.msg || JSON.stringify(r)).join('; ');
  try { return JSON.stringify(raw); } catch { return String(raw); }
};

const ValueLookups = () => {
  const [lookups, setLookups] = useState([]);
  const [selectedName, setSelectedName] = useState(null);
  const [selectedDetail, setSelectedDetail] = useState(null);
  const [entriesDraft, setEntriesDraft] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [newLookup, setNewLookup] = useState(null);

  const loadList = useCallback(async () => {
    try {
      setLookups(await listValueLookups());
    } catch (e) {
      setError(formatErr(e));
    }
  }, []);

  useEffect(() => { loadList(); }, [loadList]);

  const openLookup = useCallback(async (name) => {
    setError(null);
    setPreview(null);
    setDirty(false);
    setSelectedName(name);
    try {
      const detail = await getValueLookup(name);
      setSelectedDetail(detail);
      setEntriesDraft(
        detail.entries && detail.entries.length
          ? detail.entries.map((e) => ({ from_value: e.from_value, to_value: e.to_value }))
          : [emptyEntry()],
      );
    } catch (e) {
      setError(formatErr(e));
    }
  }, []);

  const closePanel = () => {
    if (dirty && !window.confirm('Discard unsaved entry changes?')) return;
    setSelectedName(null);
    setSelectedDetail(null);
    setEntriesDraft([]);
    setDirty(false);
    setPreview(null);
    setError(null);
  };

  const updateEntry = (idx, key, value) => {
    setEntriesDraft((prev) => prev.map((e, i) => (i === idx ? { ...e, [key]: value } : e)));
    setDirty(true);
  };

  const addRow = () => {
    setEntriesDraft((prev) => [...prev, emptyEntry()]);
    setDirty(true);
  };

  const removeRow = (idx) => {
    setEntriesDraft((prev) => prev.filter((_, i) => i !== idx));
    setDirty(true);
  };

  const saveEntries = async () => {
    const cleaned = entriesDraft
      .map((e) => ({ from_value: e.from_value.trim(), to_value: e.to_value }))
      .filter((e) => e.from_value);
    setSaving(true);
    setError(null);
    try {
      await replaceValueLookupEntries(selectedName, cleaned);
      setDirty(false);
      await openLookup(selectedName);
      await loadList();
    } catch (e) {
      setError(formatErr(e));
    } finally {
      setSaving(false);
    }
  };

  const runPreview = async () => {
    setError(null);
    try {
      setPreview(await previewValueLookup(selectedName));
    } catch (e) {
      setError(formatErr(e));
    }
  };

  const updateMetadata = async (updates) => {
    setSaving(true);
    setError(null);
    try {
      await updateValueLookup(selectedName, selectedDetail.version, {
        name: updates.name ?? selectedDetail.name,
        description: updates.description ?? selectedDetail.description,
        case_insensitive:
          updates.case_insensitive ?? selectedDetail.case_insensitive,
      });
      await openLookup(updates.name ?? selectedName);
      await loadList();
      if (updates.name && updates.name !== selectedName) {
        setSelectedName(updates.name);
      }
    } catch (e) {
      setError(formatErr(e));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (name) => {
    if (!window.confirm(`Delete lookup "${name}"? This cannot be undone.`)) return;
    setError(null);
    try {
      await deleteValueLookup(name);
      if (selectedName === name) closePanel();
      await loadList();
    } catch (e) {
      setError(formatErr(e));
    }
  };

  const startCreate = () => {
    setNewLookup({ name: '', description: '', case_insensitive: true });
    setError(null);
  };

  const cancelCreate = () => setNewLookup(null);

  const submitCreate = async () => {
    if (!newLookup?.name) return;
    setSaving(true);
    setError(null);
    try {
      const created = await createValueLookup(newLookup);
      setNewLookup(null);
      await loadList();
      if (created?.name) openLookup(created.name);
    } catch (e) {
      setError(formatErr(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="value-lookups">
      <header className="value-lookups-header">
        <h1>Value Lookups</h1>
        <button type="button" className="primary" onClick={startCreate} disabled={Boolean(newLookup)}>
          New lookup
        </button>
      </header>

      <p className="muted">
        Named tables that translate Akeneo values into BigCommerce vocabulary.
        Mappings with <code>transform=value_lookup</code> reference one of these by id.
        Unknown source values surface as validation errors on the next sync rather
        than being pushed verbatim.
      </p>

      {error && (
        <div className="card error-banner">
          <strong>Error:</strong> {errToText(error)}
          <button type="button" className="ghost" onClick={() => setError(null)}>Dismiss</button>
        </div>
      )}

      {newLookup && (
        <section className="card value-lookup-create">
          <h2>Create lookup</h2>
          <div className="grid-2">
            <label>Name
              <input
                value={newLookup.name}
                onChange={(e) => setNewLookup({ ...newLookup, name: e.target.value })}
                placeholder="lower_snake_case"
              />
              <span className="muted">Used in the API; stable identifier. Renaming later is allowed.</span>
            </label>
            <label>Description
              <input
                value={newLookup.description}
                onChange={(e) => setNewLookup({ ...newLookup, description: e.target.value })}
                placeholder="What this lookup translates"
              />
            </label>
            <label>
              <input
                type="checkbox"
                checked={newLookup.case_insensitive}
                onChange={(e) => setNewLookup({ ...newLookup, case_insensitive: e.target.checked })}
              />{' '}
              Case-insensitive
            </label>
          </div>
          <div className="actions">
            <button type="button" onClick={submitCreate} disabled={saving || !newLookup.name}>
              {saving ? 'Creating…' : 'Create'}
            </button>
            <button type="button" className="ghost" onClick={cancelCreate}>Cancel</button>
          </div>
        </section>
      )}

      <section className="card">
        <table className="value-lookups-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Description</th>
              <th>Case-insensitive</th>
              <th>Entries</th>
              <th>v</th>
              <th>Updated</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {lookups.map((l) => (
              <tr
                key={l.id}
                className={selectedName === l.name ? 'selected' : ''}
              >
                <td><code>{l.name}</code></td>
                <td className="muted">{l.description || '—'}</td>
                <td>{l.case_insensitive ? 'yes' : 'no'}</td>
                <td>{l.entry_count}</td>
                <td className="col-version">{l.version}</td>
                <td className="muted">{l.updated_at ? new Date(l.updated_at).toLocaleString() : '—'}</td>
                <td>
                  <button type="button" className="link" onClick={() => openLookup(l.name)}>
                    {selectedName === l.name ? 'Editing' : 'Edit'}
                  </button>
                  <button type="button" className="link danger" onClick={() => handleDelete(l.name)}>
                    Delete
                  </button>
                </td>
              </tr>
            ))}
            {lookups.length === 0 && (
              <tr><td colSpan={7} className="muted">No lookups yet. Click "New lookup" to add one.</td></tr>
            )}
          </tbody>
        </table>
      </section>

      {selectedDetail && (
        <section className="card value-lookup-detail">
          <header className="value-lookup-detail-header">
            <h2>
              Edit <code>{selectedDetail.name}</code>
              <small> v{selectedDetail.version}</small>
            </h2>
            <div className="value-lookup-detail-actions">
              <button type="button" onClick={runPreview}>Preview impact</button>
              <button type="button" className="ghost" onClick={closePanel}>Close</button>
            </div>
          </header>

          {preview && (
            <div className="value-lookup-preview muted">
              {preview.referenced_by_mappings?.length === 0 ? (
                <>No mapping references this lookup yet. Preview projects only the entry count: {preview.entry_count}.</>
              ) : (
                <>
                  Referenced by{' '}
                  {preview.referenced_by_mappings.map((m) => (
                    <code key={m.id}>{m.bc_target}/{m.bc_field_name}</code>
                  ))}.{' '}
                  Projecting against current Akeneo values:{' '}
                  <strong>{preview.hit_count}</strong> hit / <strong>{preview.miss_count}</strong> miss.
                  {preview.miss_count > 0 && preview.miss_count > preview.hit_count && (
                    <>{' '}<span className="warn">Most SKUs would error -- add missing entries before the next sync.</span></>
                  )}
                </>
              )}
            </div>
          )}

          <details className="value-lookup-metadata">
            <summary>Metadata (name, description, case-mode)</summary>
            <div className="grid-2">
              <label>Name
                <input
                  defaultValue={selectedDetail.name}
                  onBlur={(e) => {
                    const next = e.target.value.trim();
                    if (next && next !== selectedDetail.name) updateMetadata({ name: next });
                  }}
                />
              </label>
              <label>Description
                <input
                  defaultValue={selectedDetail.description || ''}
                  onBlur={(e) => {
                    const next = e.target.value;
                    if (next !== (selectedDetail.description || '')) updateMetadata({ description: next });
                  }}
                />
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={selectedDetail.case_insensitive}
                  onChange={(e) => updateMetadata({ case_insensitive: e.target.checked })}
                />{' '}
                Case-insensitive
              </label>
            </div>
          </details>

          <table className="value-lookup-entries-table">
            <thead>
              <tr>
                <th>Akeneo value (from)</th>
                <th>BigCommerce value (to)</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {entriesDraft.map((entry, idx) => (
                <tr key={idx}>
                  <td>
                    <input
                      value={entry.from_value}
                      onChange={(e) => updateEntry(idx, 'from_value', e.target.value)}
                      placeholder="finished_good"
                    />
                  </td>
                  <td>
                    <input
                      value={entry.to_value}
                      onChange={(e) => updateEntry(idx, 'to_value', e.target.value)}
                      placeholder="product"
                    />
                  </td>
                  <td>
                    <button type="button" className="link danger" onClick={() => removeRow(idx)}>
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
              <tr>
                <td colSpan={3}>
                  <button type="button" className="ghost" onClick={addRow}>+ Add row</button>
                </td>
              </tr>
            </tbody>
          </table>

          <div className="actions">
            <button
              type="button"
              className="primary"
              onClick={saveEntries}
              disabled={saving || !dirty}
            >
              {saving ? 'Saving…' : dirty ? 'Save entries' : 'No changes'}
            </button>
            {dirty && <span className="muted">Unsaved changes. Saves bump the lookup version.</span>}
          </div>
        </section>
      )}
    </div>
  );
};

export default ValueLookups;
